import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { getReportByToken } from '../../services/reportTrackService';

const STATUS_COLOR = {
  'On Track': 'success',
  'At Risk': 'warning',
  Delayed: 'error',
  'On Hold': 'default',
  Completed: 'success',
  'In UAT': 'info',
  'Under Support': 'default',
  'Not Started': 'default',
};

const RISK_COLOR = {
  Low: 'success',
  Medium: 'warning',
  High: 'error',
  'No Risk': 'success',
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return format(parseISO(String(d).slice(0, 10)), 'dd MMM yyyy');
  } catch {
    return d;
  }
};

const sprintRegex = /^sprint\s+[1-9][0-9]*$/i;

const getCurrentSprint = (taskClassifications = []) => {
  const active = taskClassifications
    .filter((c) => sprintRegex.test(c.name) && c.status === 'in-progress')
    .sort((a, b) => {
      const an = parseInt(a.name.split(' ')[1], 10);
      const bn = parseInt(b.name.split(' ')[1], 10);
      return bn - an;
    });
  return active[0] || null;
};

export default function WeeklyReportPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    getReportByToken(token)
      .then((res) => setData(res.data?.data))
      .catch((err) => {
        const msg =
          err?.response?.status === 404
            ? 'This report link is invalid or has expired.'
            : 'Failed to load the report. Please try again later.';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" p={3}>
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const currentSprint = getCurrentSprint(data.taskClassifications);

  const weekStart = data.weekStartDate ? parseISO(data.weekStartDate) : null;
  const weekEnd = weekStart ? new Date(weekStart.getTime() + 4 * 86400000) : null;
  const weekLabel =
    weekStart && weekEnd
      ? `${format(weekStart, 'dd MMM yyyy')} – ${format(weekEnd, 'dd MMM yyyy')}`
      : null;

  const sprintLabel = currentSprint
    ? `${currentSprint.name}${currentSprint.plannedStartDate ? ` (${formatDate(currentSprint.plannedStartDate)} – ${formatDate(currentSprint.plannedEndDate)})` : ''}`
    : '—';

  const detailRows = [
    ['Current Sprint', sprintLabel],
    ['Planned UAT Date', formatDate(data.plannedUatDate)],
    ['Planned Release Date', formatDate(data.plannedReleaseDate)],
  ];

  return (
    <>
      <Helmet>
        <title>Weekly Report – {data.projectName}</title>
      </Helmet>
      <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="sm">
          <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ bgcolor: '#1a56db', px: 3, py: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  display: 'block',
                }}
              >
                Weekly Project Report
              </Typography>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mt: 0.5 }}>
                {data.projectName}
              </Typography>
              {weekLabel && (
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mt: 0.5 }}
                >
                  {weekLabel}
                </Typography>
              )}
            </Box>

            <CardContent>
              {/* Status chips */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1.5, p: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
                    >
                      Current Status
                    </Typography>
                    <Chip
                      label={data.currentStatus}
                      color={STATUS_COLOR[data.currentStatus] || 'default'}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1.5, p: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
                    >
                      Risk Status
                    </Typography>
                    <Chip
                      label={data.riskStatus}
                      color={RISK_COLOR[data.riskStatus] || 'default'}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Key details */}
              <Stack
                divider={<Divider />}
                sx={{ border: '1px solid #e9ecef', borderRadius: 1.5, mb: 2 }}
              >
                {detailRows.map(([label, value]) => (
                  <Box key={label} sx={{ display: 'flex', px: 2, py: 1.5 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flex: '0 0 45%' }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* Key Risks */}
              {data.keyRisks?.length > 0 && (
                <Box mb={2}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    Key Risks
                  </Typography>
                  <Stack
                    spacing={0}
                    sx={{
                      border: '1px solid #ffe0e0',
                      borderRadius: 1.5,
                      bgcolor: '#fff8f8',
                      overflow: 'hidden',
                    }}
                  >
                    {data.keyRisks.map((r, i) => (
                      <Box
                        key={r.id}
                        sx={{
                          px: 2,
                          py: 1.25,
                          borderTop: i > 0 ? '1px solid #ffe0e0' : 'none',
                        }}
                      >
                        <Typography variant="body2">
                          <Box component="span" color="error.main" fontWeight={700} mr={1}>
                            &#9679;
                          </Box>
                          {r.title}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Work Completed */}
              {data.workCompletedThisWeek && (
                <Box mb={2}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    Work Completed This Week
                  </Typography>
                  <Stack
                    spacing={0}
                    sx={{
                      border: '1px solid #c8efd4',
                      borderRadius: 1.5,
                      bgcolor: '#f0fff4',
                      overflow: 'hidden',
                    }}
                  >
                    {data.workCompletedThisWeek
                      .split(/\r\n|\r|\n/)
                      .filter((l) => l.trim())
                      .map((line, i) => (
                        <Box
                          key={i}
                          sx={{
                            px: 2,
                            py: 1.25,
                            borderTop: i > 0 ? '1px solid #c8efd4' : 'none',
                          }}
                        >
                          <Typography variant="body2">
                            <Box component="span" color="success.main" fontWeight={700} mr={1}>
                              &#9679;
                            </Box>
                            {line.trim()}
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                </Box>
              )}

              {/* Next Week's Plan */}
              {data.nextWeekPlan && (
                <Box mb={2}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    Next Week&#39;s Plan
                  </Typography>
                  <Stack
                    spacing={0}
                    sx={{
                      border: '1px solid #c5d8f7',
                      borderRadius: 1.5,
                      bgcolor: '#f0f4ff',
                      overflow: 'hidden',
                    }}
                  >
                    {data.nextWeekPlan
                      .split(/\r\n|\r|\n/)
                      .filter((l) => l.trim())
                      .map((line, i) => (
                        <Box
                          key={i}
                          sx={{
                            px: 2,
                            py: 1.25,
                            borderTop: i > 0 ? '1px solid #c5d8f7' : 'none',
                          }}
                        >
                          <Typography variant="body2">
                            <Box component="span" color="primary.main" fontWeight={700} mr={1}>
                              &#9679;
                            </Box>
                            {line.trim()}
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />
              <Typography variant="caption" color="text.disabled" align="center" display="block">
                Automated weekly report from Clockies
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
}
